import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import CurrencySwitcher from "./CurrencySwitcher";
import Logo from "./Logo";
import PostunHazirTransition from "./PostunHazirTransition";

const PRIMARY_SERVICES = [
  { slug: "meta-ads", k: "services_options.meta_ads" },
  { slug: "google-ads", k: "services_options.google_ads" },
  { slug: "seo", k: "services_options.seo" },
  { slug: "creative", k: "services_options.creative" },
  { slug: "social-media", k: "services_options.social_media" },
  { slug: "web-development", k: "services_options.web_development" },
];

export default function Header() {
  const { lang, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [postunTransition, setPostunTransition] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const navLinks = [
    { to: `/${lang}/about`, label: t("nav.about") },
    { to: `/${lang}/works`, label: t("nav.works", "Çalışmalarımız") },
    { to: `/${lang}/blog`, label: t("nav.blog") },
    { to: `/${lang}/contact`, label: t("nav.contact") },
  ];

  // Cinematic Postun Hazır navigation
  const goPostunHazir = (e) => {
    e?.preventDefault?.();
    if (postunTransition) return;
    if (location.pathname.endsWith("/postun-hazir") || location.pathname.endsWith("/postun-hazir/")) {
      navigate(`/${lang}/postun-hazir`);
      return;
    }
    setPostunTransition(true);
    setTimeout(() => navigate(`/${lang}/postun-hazir`), 700);
    setTimeout(() => setPostunTransition(false), 1300);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-300 ease-out-quart
                    ${scrolled
                      ? "bg-cream/75 backdrop-blur-md border-b border-mist"
                      : "bg-cream/0 border-b border-transparent"}`}
      >
        {/* Container: controlled max-width & padding to prevent overflow */}
        <div className="w-full px-3 sm:px-6 lg:px-8 max-w-[1440px] mx-auto flex items-center justify-between py-3 gap-2 min-w-0">
          
          {/* Logo - flex-shrink-0, smaller on mobile */}
          <div className="flex-shrink-0 min-w-0">
            <Logo to={`/${lang}/`} size={26} testId="brand-logo" />
          </div>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 text-sm flex-1 justify-center">
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                type="button"
                onClick={() => setServicesOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 text-ink/80 hover:text-ink transition-colors whitespace-nowrap"
                data-testid="nav-services-trigger"
                aria-haspopup="true"
                aria-expanded={servicesOpen}
              >
                {t("nav.services")}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute top-full start-0 mt-3 min-w-[280px] rounded-2xl border border-mist
                               bg-cream/95 backdrop-blur-md shadow-xl shadow-ink/5 p-2"
                    data-testid="nav-services-menu"
                  >
                    <Link
                      to={`/${lang}/services`}
                      className="flex items-center justify-between gap-3 rounded-lg px-3.5 py-2.5
                                 text-sm font-bold text-brand-pink-deep hover:bg-brand-pink/10
                                 transition-colors border-b border-mist mb-1.5"
                      data-testid="nav-service-all"
                    >
                      <span>{t("nav.all_services", "Tüm Hizmetler — Karşılaştır")}</span>
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                    {PRIMARY_SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/${lang}/services/${s.slug}`}
                        className="flex items-center justify-between gap-3 rounded-lg px-3.5 py-2.5
                                   text-sm text-ink/80 hover:text-ink hover:bg-mist/60
                                   transition-colors group"
                        data-testid={`nav-service-${s.slug}`}
                      >
                        <span>{t(s.k)}</span>
                        <ArrowUpRight className="h-3.5 w-3.5 text-ink/40 group-hover:text-brand-pink transition-colors" />
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-ink/80 hover:text-ink transition-colors whitespace-nowrap"
                data-testid={`nav-link-${l.to.split("/").pop()}`}
              >
                {l.label}
              </Link>
            ))}

            {/* Postun Hazır — logo + cinematic */}
            <a
              href={`/${lang}/postun-hazir`}
              onClick={goPostunHazir}
              data-testid="nav-postun-hazir"
              className="ph-nav-link group relative inline-flex items-center gap-2 rounded-full
                         px-3 py-1 text-postun-blue-deep
                         transition-[transform,color] duration-300 hover:-translate-y-0.5"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-postun-cyan opacity-75 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-postun-cyan" />
              </span>
              <img src="/brand/postun-hazir-logo.png" alt="" loading="lazy"
                className="h-7 w-7 object-contain" aria-hidden="true" />
              <span className="font-semibold whitespace-nowrap">Postun Hazır</span>
            </a>
          </nav>

          {/* Right controls - always visible, flex-shrink-0 */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <div className="hidden xs:flex sm:flex"><CurrencySwitcher /></div>
            <LanguageSwitcher />
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden inline-flex h-9 w-9 min-w-[36px] items-center justify-center rounded-full border border-ink/15 text-ink flex-shrink-0"
              aria-label={t("nav.open_menu")}
              data-testid="mobile-menu-trigger"
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileOpen(false)}
          >
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="absolute end-0 top-0 h-full w-[88%] max-w-[420px] bg-cream flex flex-col overflow-y-auto"
              data-testid="mobile-drawer"
            >
              {/* Header row */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-mist flex-shrink-0">
                <Logo to={`/${lang}/`} size={28} testId="mobile-drawer-logo" onClick={() => setMobileOpen(false)} />
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-ink/15"
                  aria-label={t("nav.close_menu")}
                  data-testid="mobile-menu-close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <nav className="flex-1 px-6 py-4 space-y-1">
                {/* Hizmetler accordion */}
                <div>
                  <button
                    type="button"
                    onClick={() => setMobileServicesOpen((v) => !v)}
                    className="flex items-center justify-between w-full py-3 border-b border-mist text-base font-medium text-ink"
                    data-testid="mobile-services-toggle"
                  >
                    <span>{t("nav.services")}</span>
                    <ChevronDown className={`h-4 w-4 text-ink/50 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {mobileServicesOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-1 pb-2 space-y-0.5">
                          <Link
                            to={`/${lang}/services`}
                            className="flex items-center justify-between px-3 py-2 rounded-lg text-sm font-bold text-brand-pink-deep hover:bg-brand-pink/10"
                            data-testid="mobile-nav-service-all"
                          >
                            {t("nav.all_services", "Tüm Hizmetler")}
                            <ArrowUpRight className="h-3.5 w-3.5" />
                          </Link>
                          {PRIMARY_SERVICES.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/${lang}/services/${s.slug}`}
                              className="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-ink/70 hover:text-ink hover:bg-mist/60"
                              data-testid={`mobile-nav-service-${s.slug}`}
                            >
                              {t(s.k)}
                              <ArrowUpRight className="h-3.5 w-3.5 text-ink/30" />
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Main nav links */}
                {navLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="flex items-center justify-between py-3 border-b border-mist text-base text-ink"
                    data-testid={`mobile-nav-link-${l.to.split("/").pop()}`}
                  >
                    {l.label}
                  </Link>
                ))}

                {/* Postun Hazır — same style as desktop nav (pill with logo) */}
                <a
                  href={`/${lang}/postun-hazir`}
                  onClick={(e) => { setMobileOpen(false); goPostunHazir(e); }}
                  className="mt-4 inline-flex items-center gap-2 rounded-full
                             px-4 py-2.5 text-postun-blue-deep border border-postun-blue/30
                             bg-postun-blue/5 hover:bg-postun-blue hover:text-cream
                             transition-colors font-semibold text-sm"
                  data-testid="mobile-nav-postun-hazir"
                >
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-postun-cyan opacity-75 animate-ping" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-postun-cyan" />
                  </span>
                  <img src="/brand/postun-hazir-logo.png" alt="" className="h-6 w-6 object-contain" />
                  Postun Hazır
                </a>
              </nav>

              {/* Bottom controls */}
              <div className="px-6 py-4 border-t border-mist flex items-center gap-3 flex-shrink-0">
                <CurrencySwitcher />
                <LanguageSwitcher />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cinematic Postun Hazır transition overlay */}
      <PostunHazirTransition active={postunTransition} />
    </>
  );
}
